import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, orderBy, query } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { 
  PlusIcon, 
  DocumentIcon, 
  TrashIcon, 
  PencilIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AdminArticles = () => {
  const { userProfile } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: 'Inspiration',
    tags: '',
    pdf_file: null,
    featured: false,
    published: true
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, orderBy('created_time', 'desc'));
      const snapshot = await getDocs(q);
      const articlesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  const uploadFile = async (file, path) => {
    if (!file) return null;
    
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const timestamp = new Date();
      let pdfUrl = null;

      // Upload PDF file
      if (formData.pdf_file) {
        const pdfPath = `articles/pdfs/${Date.now()}_${formData.pdf_file.name}`;
        pdfUrl = await uploadFile(formData.pdf_file, pdfPath);
      }

      const articleData = {
        title: formData.title || '',
        excerpt: formData.excerpt || '',
        content: formData.content || '',
        author: formData.author || '',
        category: formData.category || 'Inspiration',
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        featured: formData.featured || false,
        published: formData.published !== undefined ? formData.published : true,
        created_time: timestamp,
        updated_time: timestamp,
        created_by: userProfile.uid
      };

      // Add URL if PDF was uploaded
      if (pdfUrl) articleData.pdf_url = pdfUrl;

      // Filter out undefined values
      const cleanArticleData = Object.fromEntries(
        Object.entries(articleData).filter(([_, value]) => value !== undefined)
      );

      if (editingArticle) {
        // Update existing article
        await updateDoc(doc(db, 'articles', editingArticle.id), {
          ...cleanArticleData,
          updated_time: timestamp
        });
        toast.success('Article updated successfully');
      } else {
        // Create new article
        await addDoc(collection(db, 'articles'), cleanArticleData);
        toast.success('Article created successfully');
      }

      // Reset form
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        author: '',
        category: 'Inspiration',
        tags: '',
        pdf_file: null,
        featured: false,
        published: true
      });
      setShowForm(false);
      setEditingArticle(null);
      fetchArticles();

    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      author: article.author,
      category: article.category || 'Inspiration',
      tags: article.tags ? article.tags.join(', ') : '',
      pdf_file: null,
      featured: article.featured || false,
      published: article.published !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (article) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      // Delete files from storage if they exist
      if (article.pdf_url) {
        const pdfRef = ref(storage, article.pdf_url);
        await deleteObject(pdfRef).catch(console.error);
      }

      // Delete document from Firestore
      await deleteDoc(doc(db, 'articles', article.id));
      toast.success('Article deleted successfully');
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Dauntless Articles</h1>
            <p className="text-gray-600 mt-2">Upload and manage PDF articles for your club</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Article
          </button>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Articles ({articles.length})</h2>
          </div>
          
          {articles.length === 0 ? (
            <div className="p-8 text-center">
              <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No articles yet. Create your first Dauntless article!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {articles.map((article) => (
                <div key={article.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">{article.title}</h3>
                        {article.featured && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            Featured
                          </span>
                        )}
                        {article.pdf_url && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            PDF
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          article.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {article.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 mb-2">{article.excerpt}</p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>By {article.author}</span>
                        <span>•</span>
                        <span>{article.category}</span>
                        <span>•</span>
                        <span>{formatDate(article.created_time)}</span>
                      </div>
                      
                      {article.tags && article.tags.length > 0 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1">
                            {article.tags.map((tag, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(article)}
                        className="p-2 text-gray-400 hover:text-primary-600"
                        title="Edit article"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(article)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete article"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Article Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-full overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">
                    {editingArticle ? 'Edit Article' : 'Create New Article'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingArticle(null);
                      setFormData({
                        title: '',
                        excerpt: '',
                        content: '',
                        author: '',
                        category: 'Inspiration',
                        tags: '',
                        pdf_file: null,
                        featured: false,
                        published: true
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Article title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt *
                    </label>
                    <textarea
                      name="excerpt"
                      required
                      rows="3"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Brief description of the article"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Content
                    </label>
                    <textarea
                      name="content"
                      rows="6"
                      value={formData.content}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Full article content (optional if PDF is provided)"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Author *
                      </label>
                      <input
                        type="text"
                        name="author"
                        required
                        value={formData.author}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="Author name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="input-field"
                      >
                        <option value="Inspiration">Inspiration</option>
                        <option value="Training">Training</option>
                        <option value="Nutrition">Nutrition</option>
                        <option value="Competition">Competition</option>
                        <option value="Community">Community</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="motivation, running, perseverance"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PDF File *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <input
                        type="file"
                        name="pdf_file"
                        accept=".pdf"
                        onChange={handleInputChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Upload a PDF file for the article
                      </p>
                    </div>
                  </div>


                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Featured Article</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="published"
                        checked={formData.published}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Published</span>
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setEditingArticle(null);
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={uploading}
                      className="btn-primary inline-flex items-center"
                    >
                      {uploading ? (
                        <>
                          <div className="spinner mr-2"></div>
                          {editingArticle ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                          {editingArticle ? 'Update Article' : 'Create Article'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminArticles;
